/* eslint semi: 1 */
import EventBus from '@/components/mixins/event-bus-mixin';
import AuthMixin from '@/components/mixins/auth-mixin';
import UnsavedDataMixin from '@/components/mixins/unsaved-data-mixin';
import Fuse from 'fuse.js';
import { useMediaListStore } from '@/pinia/stores/media/mediaListStore';
import { useMediaQueryStore } from '@/pinia/stores/media/mediaQueryStore';
import { useMediaStore } from '@/pinia/stores/media/mediaStore';
import { useMediaSelectionStore } from '@/pinia/stores/media/mediaSelectionStore';
import { useFileUploadProgressStore } from '@/pinia/stores/file-upload/fileUploadProgressStore';
import { useUserStore } from '@base-shared/stores/user';
import { useGroupStore } from '@base-shared/stores/group';

export default {
  name: 'userAccountsDropdown',
  inject: [
    '$currentUser',
    '$utils',
    '$api',
    '$apiError',
    '$signedInUserDetails',
    '$signedInUserInfo',
    '$signedInUserAccounts'
  ],
  components: {
  },
  props: {},
  watch: {
    $route: {
      handler (val, oldVal) {
        this.setDefaultData();
      },
      deep: true
    },
    /**
     * Watches the `userStore.currentUser` for changes. Replacement
     * for the old `BIND_USER_DETAILS_IN_VUE` from EventBus.
     */
    'userStore.currentUser': {
      handler (newValue) {
        this.setDefaultData();
      },
      deep: true
    },
    /**
     * Watches the `userStore.accounts` for changes. Replacement
     * for the old `BIND_USER_ACCOUNTS_IN_VUE` from EventBus.
     */
    'userStore.accounts': {
      handler (newValue) {
        this.setDefaultData();
      },
      deep: true
    },
    /**
     * Watches the `groupStore.groupInfo` for changes. Replacement
     * for the old `USER_INFO_AVAILABLE_IN_VUE` from EventBus.
     */
    'groupStore.groupInfo': {
      handler (newValue) {
        this.setDefaultData();
      },
      deep: true
    }
  },
  mixins: [AuthMixin, UnsavedDataMixin],
  setup () {
    // return to expose to template or Options API methods
    return {
      userStore: useUserStore(),
      groupStore: useGroupStore(),
      mediaListStore: useMediaListStore(),
      mediaQueryStore: useMediaQueryStore(),
      mediaStore: useMediaStore(),
      mediaSelectionStore: useMediaSelectionStore(),
      uploaderProgressStore: useFileUploadProgressStore()
    };
  },
  data () {
    return {
      showTopSettingsWrapper: false,
      title: 'default-layout-structure-page',
      accountOptions: [],
      selectedAccountGroupId: '',
      currentUserGroupId: null,
      currentAccountName: '',
      showUserAccountDropdown: false,
      showUpgradeButton: false,
      showSettingsButton: false,
      userInfo: null,
      searchInput: ''
    };
  },
  computed: {
    filteredAccountOptions () {
      // NOTE: We decided to not show currently selected account
      const options = this.accountOptions.filter(item => item.groupId !== this.currentUserGroupId);

      if (!this.searchInput) {
        return options;
      }

      const fuse = new Fuse(options, {
        includeScore: true,
        findAllMatches: true,
        keys: ['name'],
        ignoreLocation: true,
        threshold: 0.35,
      });

      const result = fuse.search(this.searchInput);
      return result.map(data => data.item);
    }
  },
  methods: {
    resetPiniaStores () {
      this.mediaListStore.reset();
      this.mediaQueryStore.reset();
      this.mediaStore.reset();
      this.mediaSelectionStore.reset();
      this.uploaderProgressStore.reset();
    },
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('user-accounts-dropdown-change', { action, payload: { data } });
      }
    },
    handleAccountDropdownChanged (value) {
      this.selectedAccountGroupId = value;
      this.checkUnsavedData({
        nextAction: () => {
          this.currentAccountName = this.getAccountNameFromOptions(this.selectedAccountGroupId);
          this.handleSwitchAccount(this.selectedAccountGroupId);
        }
      });
    },
    handleSwitchAccount (accountGroupId) {
      if (!accountGroupId || (this.currentUserGroupId === accountGroupId)) {
        return;
      }

      const switchAccRequest = () => {
        this.$utils.mainLoader(true);

        this.$api.put(`${CONFIG.API_BASE_URL}/group/accounts/${accountGroupId}`)
          .then(response => {
            this.refreshUserSession({
              keepLoaderVisible: true,
              nextAction: async () => {
                // Reset media pinia stores to reset pagination
                // and filters when switching account.
                this.resetPiniaStores();
                // Redirect to account switched page
                if (!this.$route.fullPath.toLowerCase().includes('account-switched')) {
                  await this.$router.push('/account-switched');
                }

                this.$utils.mainLoader(false);
              }
            });
          })
          .catch((error) => {
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { switchAccRequest(); }, showDialogNotification: true });
            this.$utils.mainLoader(false);
          });
      };
      switchAccRequest();
    },
    getAccountNameFromOptions (groupId) {
      let resAccountName = 'None';
      const getAccountName = this.accountOptions.find((acc) => { return acc.groupId === groupId; });

      if (getAccountName) {
        resAccountName = getAccountName.name;
      }

      return resAccountName;
    },
    handleAccountDropdownVisibleChanged (isVisible) {
      // Workaround: El-Select does not remove focus when closing the dropdown on the second click.
      // To handle this, trigger the blur function when dropdown is hidden (isVisible = false)
      if (!isVisible && this.$refs.userAccountsSelect) {
        this.$refs.userAccountsSelect.blur();
        this.searchInput = '';

        return;
      }

      this.$nextTick(() => {
        // Focus the search input element
        this.$refs.searchInputElement.focus();
      });
    },
    handleAccountNameHover ($event, type) {
      const targetEl = $($($event.target).parent());
      const textEl = $($($event.target).parent()).find('.text');
      let showTooltip = textEl.length && (textEl[0].offsetWidth < textEl[0].scrollWidth);

      if (type === 'mouseover' && showTooltip) {
        showTooltip = true;
      } else {
        showTooltip = false;
      }

      this.$utils.handleCustomTooltip({ targetEl, showTooltip, fullText: textEl.text() });
    },
    setUpgradeButtonVisibility () {
      // If the user has no access to the product, hide the upgrade button
      const hasProductAccess = this.$currentUser.hasAccessToSelectedProduct();
      if (!hasProductAccess) {
        this.showUpgradeButton = false;
        return;
      }

      const currentPlan = (this.$signedInUserInfo && this.$signedInUserInfo._edit) ? this.$signedInUserInfo._edit.plan.currentPlan : null;
      if (currentPlan === this.$utils.options.plans.DISCOVERY && !this.$utils.isPageForLicenses()) {
        this.showUpgradeButton = true;
      } else {
        this.showUpgradeButton = false;
      }
    },
    setSettingsButtonVisibility () {
      // If the user has no access to the product, hide the settings button
      const hasProductAccess = this.$currentUser.hasAccessToSelectedProduct();
      this.showSettingsButton = hasProductAccess;
    },
    setDefaultData () {
      this.showUserAccountDropdown = false;
      this.currentUserGroupId = this.$currentUser.getUserGroupId() ?? null;
      this.showTopSettingsWrapper = !!this.$signedInUserDetails;
      this.userInfo = this.$signedInUserInfo;

      // Set User Accounts
      if (this.$signedInUserAccounts && this.$signedInUserAccounts.length > 1) {
        this.showUserAccountDropdown = true;
        this.accountOptions = this.$signedInUserAccounts;
        this.currentAccountName = this.getAccountNameFromOptions(this.currentUserGroupId);
        this.selectedAccountGroupId = this.currentUserGroupId;
      }

      // Set Upgrade Button visibility
      this.setUpgradeButtonVisibility();

      // Set Settings Button visibility
      this.setSettingsButtonVisibility();
    }
  },
  mounted () {
    // Listen SignedIn from Global Event Bus
    EventBus.$on('SIGNED_IN', (payload) => {
      this.setDefaultData();

      if (this.$utils.isSignUpDisallowed()) {
        this.showUpgradeButton = false;
      }
    });
  }
};
